<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import Button from 'primevue/button';
import Chart from 'primevue/chart';
import CheckIcon from '@primeicons/vue/check';
import ConfirmDialog from 'primevue/confirmdialog';
import DatePicker from 'primevue/datepicker';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import IftaLabel from 'primevue/iftalabel';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import MapMarkerIcon from '@primeicons/vue/map-marker';
import ProgressSpinner from 'primevue/progressspinner';
import SaveIcon from '@primeicons/vue/save';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import TrashIcon from '@primeicons/vue/trash';
import { useConfirm } from 'primevue/useconfirm';
import type { ChartOptions, TooltipItem } from 'chart.js';
import { useEndpoint } from '@apparatix/composables';
import { Section } from '@apparatix/library/core';
import { pricingDescriptions } from '@apparatix/stores/usePricingStore';
import { useHalFeatures } from '@apparatix/stores/useHalFeatures';
import {
  CONTEXT_OPTIONS, CONTEXT_STYLES, formatCurrency, parseDate, toDateString,
} from '@apparatix/utilities/pricingFormatting';
import type {
  InventoryPricingComponentOption,
  InventoryPricingResponse,
  InventoryPricingValue,
  PricingHistoryResponse,
  PricingHistorySeries,
  PricingHistoryVersion,
  SalesContextCode,
} from './types';

const CHART_PALETTE = ['#3B82F6', '#F97316', '#10B981', '#8B5CF6', '#EF4444', '#0EA5E9', '#EAB308', '#EC4899'];

const props = defineProps<{ id: number }>();

type ScheduledRow = InventoryPricingValue & {
  amountValue: number;
  effectiveDate: Date;
  originalAmount: number;
  originalEffectiveStartDate: string;
  originalNotes: string;
};

type EditableRow = {
  componentKey: string;
  componentLabel: string;
  role: 'standard' | 'floor';
  label: string;
  amount: number;
  originalAmount: number;
  basis: string;
  effectiveStartDate: string | null;
  isSet: boolean;
  newEffectiveDate: Date | null;
  notes: string;
  scheduled: ScheduledRow[];
};

const halFeatures = useHalFeatures();
// hasFeature() returns undefined until /hal-features resolves; treat that as
// "don't warn yet" so the banner doesn't flash on load for tenants where yield is enabled.
const yieldEnabled = computed(() => halFeatures.hasFeature('feature-yield-codes') !== false);

const context = ref<SalesContextCode>('local');
const contextStyle = computed(() => CONTEXT_STYLES[context.value]);
const rows = ref<EditableRow[]>([]);
const asOfDate = ref('');
const scheduledSavingId = ref<number | null>(null);
const scheduledDeletingId = ref<number | null>(null);
const confirm = useConfirm();

const formatDate = (date: string | null): string => {
  if (date === null) {
    return 'No record yet';
  }

  return parseDate(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const tomorrow = computed(() => {
  if (!asOfDate.value) {
    return new Date();
  }

  const date = parseDate(asOfDate.value);
  date.setDate(date.getDate() + 1);
  return date;
});

const toScheduledRow = (value: InventoryPricingValue): ScheduledRow => ({
  ...value,
  amountValue: Number(value.amount),
  effectiveDate: parseDate(value.effectiveStartDate),
  originalAmount: Number(value.amount),
  originalEffectiveStartDate: value.effectiveStartDate,
  originalNotes: value.notes ?? '',
  notes: value.notes ?? '',
});

const toRow = (
  component: InventoryPricingComponentOption,
  role: 'standard' | 'floor',
  label: string,
  data: InventoryPricingResponse,
): EditableRow => {
  const value = data.values[`${component.key}:${role}`];

  return {
    componentKey: component.key,
    componentLabel: component.label,
    role,
    label,
    amount: value ? Number(value.amount) : 0,
    originalAmount: value ? Number(value.amount) : 0,
    basis: value?.basis ?? component.defaultBasis,
    effectiveStartDate: value?.effectiveStartDate ?? null,
    isSet: !!value,
    newEffectiveDate: null,
    notes: '',
    scheduled: data.scheduledValues
      .filter(scheduledValue => scheduledValue.componentKey === component.key && scheduledValue.role === role)
      .map(toScheduledRow),
  };
};

const applyResponse = (data: InventoryPricingResponse) => {
  asOfDate.value = data.asOfDate;
  rows.value = data.components.flatMap(component => {
    const componentRows = [toRow(component, 'standard', component.label, data)];

    if (data.permissions.canEditFloor && component.hasFloor) {
      componentRows.push(toRow(component, 'floor', `${component.label} Floor`, data));
    }

    return componentRows;
  });
};

const { isLoading, makeRequest, setEndpointUrl } = useEndpoint<{ data: InventoryPricingResponse }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => applyResponse(response.data),
  toast: { showOnSuccess: false },
});

const {
  isLoading: isSaving,
  makeRequest: saveRequest,
  setEndpointUrl: setSaveEndpoint,
} = useEndpoint<{ data: InventoryPricingResponse }>({
  url: '',
  type: 'POST',
  successField: 'data',
  onSuccess: response => {
    applyResponse(response.data);
    fetchHistory();
  },
  toast: { showOnSuccess: true, successMessage: 'Pricing saved' },
});

const {
  makeRequest: updateScheduledRequest,
  setEndpointUrl: setUpdateScheduledEndpoint,
} = useEndpoint<{ data: InventoryPricingResponse }>({
  url: '',
  type: 'PUT',
  successField: 'data',
  onSuccess: response => {
    applyResponse(response.data);
    scheduledSavingId.value = null;
    fetchHistory();
  },
  toast: { showOnSuccess: true, successMessage: 'Scheduled change updated' },
});

const {
  makeRequest: deleteScheduledRequest,
  setEndpointUrl: setDeleteScheduledEndpoint,
} = useEndpoint<{ data: InventoryPricingResponse }>({
  url: '',
  type: 'DELETE',
  successField: 'data',
  onSuccess: response => {
    applyResponse(response.data);
    scheduledDeletingId.value = null;
    fetchHistory();
  },
  toast: { showOnSuccess: true, successMessage: 'Scheduled change deleted' },
});

const isRowDirty = (row: EditableRow): boolean => row.amount !== row.originalAmount;

const dirtyRows = computed(() => rows.value.filter(isRowDirty));
const hasChanges = computed(() => dirtyRows.value.length > 0);

const save = () => {
  if (!hasChanges.value) {
    return;
  }

  const values = dirtyRows.value.map(row => ({
    componentKey: row.componentKey,
    role: row.role,
    amount: row.amount,
    effectiveStartDate: toDateString(row.newEffectiveDate),
    notes: row.notes || null,
  }));

  saveRequest({ context: context.value, values });
};

const isScheduledDirty = (row: ScheduledRow): boolean =>
  row.amountValue !== row.originalAmount
  || toDateString(row.effectiveDate) !== row.originalEffectiveStartDate
  || row.notes !== row.originalNotes;

const updateScheduled = async (row: ScheduledRow) => {
  if (!isScheduledDirty(row)) {
    return;
  }

  scheduledSavingId.value = row.profileId;
  setUpdateScheduledEndpoint(
    `/manage-pricing/inventory/${props.id}/pricing/${row.profileId}`,
  );

  await updateScheduledRequest({
    amount: row.amountValue,
    effectiveStartDate: toDateString(row.effectiveDate),
    notes: row.notes || null,
  });

  scheduledSavingId.value = null;
};

const confirmDeleteScheduled = (row: ScheduledRow) => {
  confirm.require({
    group: 'delete-scheduled-pricing',
    header: 'Delete scheduled pricing?',
    message: `Delete the ${row.role} change scheduled for ${formatDate(row.effectiveStartDate)}?`,
    icon: ExclamationTriangleIcon,
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      scheduledDeletingId.value = row.profileId;
      setDeleteScheduledEndpoint(
        `/manage-pricing/inventory/${props.id}/pricing/${row.profileId}`,
      );
      await deleteScheduledRequest();
      scheduledDeletingId.value = null;
    },
  });
};

// --- Standard-rate history chart (all components at once) -----------------

const historySeries = ref<PricingHistorySeries[]>([]);

const {
  isLoading: isHistoryLoading,
  makeRequest: historyRequest,
  setEndpointUrl: setHistoryEndpoint,
} = useEndpoint<{ data: PricingHistoryResponse }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    historySeries.value = response.data.series;
  },
  toast: { showOnSuccess: false },
});

const hasHistory = computed(() => historySeries.value.some(series => series.versions.length > 0));

const isHistoryFullyLoaded = ref(false);

const fetchHistory = () => {
  const params = new URLSearchParams({ context: context.value });

  if (isHistoryFullyLoaded.value) {
    params.set('loadAll', '1');
  }

  setHistoryEndpoint(`/manage-pricing/inventory/${props.id}/pricing-history?${params.toString()}`);
  historyRequest();
};

const loadAllHistory = () => {
  isHistoryFullyLoaded.value = true;
  fetchHistory();
};

watch(context, fetchHistory);

const chronologicalSeries = computed(() => historySeries.value.map(series => ({
  ...series,
  versions: [...series.versions].sort((a, b) => a.effectiveStartDate.localeCompare(b.effectiveStartDate)),
})));

const todayDate = computed(() => asOfDate.value || toDateString(new Date()) as string);

const chartLabels = computed(() => {
  const dates = chronologicalSeries.value.flatMap(series => series.versions.map(version => version.effectiveStartDate));

  if (dates.length > 0) {
    dates.push(todayDate.value);
  }

  return [...new Set(dates)].sort();
});

const applicableVersion = (series: PricingHistorySeries, date: string): PricingHistoryVersion | undefined =>
  [...series.versions].reverse().find(version => version.effectiveStartDate <= date);

const componentColors = computed(() => {
  const colors = new Map<string, string>();

  chronologicalSeries.value.forEach(series => {
    if (!colors.has(series.componentKey)) {
      colors.set(series.componentKey, CHART_PALETTE[colors.size % CHART_PALETTE.length]);
    }
  });

  return colors;
});

const chartData = computed(() => ({
  labels: chartLabels.value,
  datasets: chronologicalSeries.value.map(series => {
    const color = componentColors.value.get(series.componentKey);

    return {
      label: series.role === 'floor' ? `${series.componentLabel} Floor` : series.componentLabel,
      data: chartLabels.value.map(date => {
        const version = applicableVersion(series, date);
        return version ? Number(version.amount) : null;
      }),
      fill: false,
      borderColor: color,
      backgroundColor: color,
      borderDash: series.role === 'floor' ? [6, 4] : undefined,
      stepped: true,
      spanGaps: true,
    };
  }),
}));

const chartOptions = computed<ChartOptions<'line'>>(() => {
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-950');
  const labels = chartLabels.value;
  const series = chronologicalSeries.value;

  return {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { color: textColor }},
      tooltip: {
        callbacks: {
          label: ctx => ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
          afterLabel: (ctx: TooltipItem<'line'>) => {
            const version = applicableVersion(series[ctx.datasetIndex], labels[ctx.dataIndex]);

            if (!version) {
              return '';
            }

            const lines = [
              `Set by: ${version.createdBy ?? version.sourceType}`,
              `Effective: ${version.effectiveStartDate}`,
            ];

            if (version.notes) {
              lines.push(`Notes: ${version.notes}`);
            }

            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: textColorSecondary },
        grid: { color: surfaceBorder },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          callback: value => formatCurrency(value as number),
        },
        grid: { color: surfaceBorder },
      },
    },
  };
});

const loadUnit = () => {
  setEndpointUrl(`/manage-pricing/inventory/${props.id}/pricing?context=${context.value}`);
  setSaveEndpoint(`/manage-pricing/inventory/${props.id}/pricing`);
  makeRequest();
  fetchHistory();
};

watch(() => props.id, loadUnit);
watch(context, loadUnit);
onMounted(loadUnit);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex flex-wrap items-center justify-between gap-3 rounded-md border-l-4 px-3 py-2 transition-colors"
      :class="contextStyle.banner"
    >
      <div class="flex items-center gap-2 text-sm font-bold">
        <MapMarkerIcon />
        {{ contextStyle.label }}
        <InfoCircleIcon
          v-if="context === 'national'"
          v-tooltip="pricingDescriptions.automatedSales"
          color="var(--tw-text-gray-500)"
          class="cursor-pointer"
        />
      </div>
      <Select
        v-model="context"
        :options="CONTEXT_OPTIONS"
        option-label="name"
        option-value="code"
        class="w-40"
      />
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
    </div>

    <template v-else>
      <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div
          v-for="row in rows"
          :key="`${row.componentKey}:${row.role}`"
          class="flex flex-col gap-3 border-b border-gray-200 p-3 last:border-b-0 lg:grid lg:grid-cols-[minmax(8rem,1fr)_6rem_10rem_minmax(8rem,1fr)_auto] lg:items-start lg:gap-3"
        >
          <div class="flex items-center gap-2 lg:pt-2">
            <h3 class="text-sm font-bold text-gray-800">
              {{ row.label }}
            </h3>
            <InfoCircleIcon
              v-if="row.role === 'floor'"
              v-tooltip="pricingDescriptions.floorTooltip(row.componentKey, row.componentLabel)"
              color="var(--tw-text-gray-500)"
              class="cursor-pointer"
            />
            <Tag
              v-if="row.scheduled.length"
              :value="`${row.scheduled.length} scheduled`"
              severity="info"
            />
          </div>

          <div class="flex min-w-0 flex-col gap-1">
            <IftaLabel>
              <InputNumber
                :id="`rate-${row.componentKey}-${row.role}`"
                v-model="row.amount"
                mode="currency"
                currency="USD"
                fluid
                :min="0"
                :max-fraction-digits="2"
                class="w-full"
                :input-class="row.amount !== row.originalAmount ? 'font-bold' : ''"
              />
              <label :for="`rate-${row.componentKey}-${row.role}`">Rate</label>
            </IftaLabel>
            <span class="text-xs text-gray-500">
              <template v-if="row.role === 'floor'">
                {{ row.isSet ? `Since ${formatDate(row.effectiveStartDate)}` : 'No floor in effect' }}
              </template>
              <template v-else>
                {{ row.isSet ? row.basis.replace('_', ' ') : `No price in effect (${row.basis.replace('_', ' ')})` }}
              </template>
            </span>
          </div>

          <div class="flex min-w-0 flex-col gap-1">
            <IftaLabel>
              <DatePicker
                :id="`effective-date-${row.componentKey}-${row.role}`"
                v-model="row.newEffectiveDate"
                show-icon
                fluid
                date-format="yy-mm-dd"
                :min-date="tomorrow"
                class="w-full"
              />
              <label :for="`effective-date-${row.componentKey}-${row.role}`">Effective Date</label>
            </IftaLabel>
          </div>

          <div class="flex min-w-0 flex-col gap-1">
            <IftaLabel>
              <InputText
                :id="`notes-${row.componentKey}-${row.role}`"
                v-model="row.notes"
                class="w-full"
              />
              <label :for="`notes-${row.componentKey}-${row.role}`">Notes</label>
            </IftaLabel>
          </div>

          <Button
            :loading="isSaving && isRowDirty(row)"
            :disabled="!isRowDirty(row) || isSaving"
            @click="save"
          >
            <SaveIcon />
            Save
          </Button>

          <div
            v-if="row.componentKey === 'ad_space' && row.role === 'floor' && !yieldEnabled"
            class="col-span-full flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
          >
            <ExclamationTriangleIcon class="shrink-0" />
            Yield is not enabled, so this floor will not take effect until Yield is turned on.
          </div>

          <div v-if="row.scheduled.length" class="col-span-full flex flex-col gap-2 border-t border-gray-200 pt-3">
            <div class="flex items-center justify-between gap-3">
              <h4 class="text-xs font-bold uppercase tracking-wide text-gray-600">
                Upcoming changes
              </h4>
              <span class="text-xs text-gray-500">Editable until effective</span>
            </div>

            <div
              v-for="scheduled in row.scheduled"
              :key="scheduled.profileId"
              class="grid grid-cols-1 items-start gap-3 border-t border-gray-100 p-2 sm:grid-cols-[7rem_10rem_minmax(0,1fr)_auto]"
            >
              <div class="flex min-w-0 flex-col gap-1">
                <IftaLabel>
                  <InputNumber
                    :id="`scheduled-rate-${scheduled.profileId}`"
                    v-model="scheduled.amountValue"
                    mode="currency"
                    currency="USD"
                    fluid
                    :min="0"
                    :max-fraction-digits="2"
                    class="w-full"
                  />
                  <label :for="`scheduled-rate-${scheduled.profileId}`">Rate</label>
                </IftaLabel>
                <Tag
                  :value="scheduled.role === 'floor' ? 'Floor' : 'Standard'"
                  :severity="scheduled.role === 'floor' ? 'warn' : 'info'"
                  class="w-fit"
                />
              </div>
              <div class="flex min-w-0 flex-col gap-1">
                <IftaLabel>
                  <DatePicker
                    :id="`scheduled-effective-date-${scheduled.profileId}`"
                    v-model="scheduled.effectiveDate"
                    show-icon
                    fluid
                    date-format="yy-mm-dd"
                    :min-date="tomorrow"
                    class="w-full"
                  />
                  <label :for="`scheduled-effective-date-${scheduled.profileId}`">Effective Date</label>
                </IftaLabel>
              </div>
              <div class="flex min-w-0 flex-col gap-1">
                <IftaLabel>
                  <InputText
                    :id="`scheduled-notes-${scheduled.profileId}`"
                    v-model="scheduled.notes"
                    class="w-full"
                  />
                  <label :for="`scheduled-notes-${scheduled.profileId}`">Notes</label>
                </IftaLabel>
              </div>
              <div class="flex flex-col gap-1">
                <label class="invisible text-xs font-medium">Actions</label>
                <div class="flex justify-end gap-2">
                  <Button
                    v-tooltip.top="'Save scheduled change'"
                    outlined
                    icon-only
                    :loading="scheduledSavingId === scheduled.profileId"
                    :disabled="!isScheduledDirty(scheduled) || scheduledDeletingId === scheduled.profileId"
                    aria-label="Save scheduled change"
                    @click="updateScheduled(scheduled)"
                  >
                    <CheckIcon />
                  </Button>
                  <Button
                    v-tooltip.top="'Delete scheduled change'"
                    severity="danger"
                    outlined
                    icon-only
                    :loading="scheduledDeletingId === scheduled.profileId"
                    :disabled="scheduledSavingId === scheduled.profileId"
                    aria-label="Delete scheduled change"
                    @click="confirmDeleteScheduled(scheduled)"
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Section :footer="false">
        <template #title>
          <div class="flex items-center justify-between">
            <h3 class="text-base font-bold text-gray-700">
              History
            </h3>
            <Button
              v-if="!isHistoryFullyLoaded"
              v-tooltip.top="'Only the last 2 years of history are shown by default. Click here to load all history.'"
              text
              size="small"
              @click="loadAllHistory"
            >
              Load All
            </Button>
          </div>
        </template>

        <div v-if="isHistoryLoading" class="flex justify-center py-10">
          <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
        </div>
        <div v-else-if="!hasHistory" class="py-10 text-center text-gray-500">
          No pricing history recorded yet.
        </div>
        <div v-else class="relative h-[22rem] w-full">
          <Chart
            type="line"
            :data="chartData"
            :options="chartOptions"
            class="h-full w-full"
          />
        </div>
      </Section>
    </template>

    <ConfirmDialog group="delete-scheduled-pricing" />
  </div>
</template>
